import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { integrationModel } from '~/models';
import { EmailOnBehalfTypes } from '~/models/EmailOnBehalfSettingsModel';
import { getEmailProviderDetails, Tab } from '~/pages/NotificationSettings/NotificationSettings.utils';
import { selectors } from '~/store/state-selectors';
import IntegrationCard from '../IntegrationCard/IntegrationCard';
import OutlookLogo from '../logos/outlook.svg';
export const GetOutlookCardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            OutlookCardTID: {
                Card: 'outlook-card',
                Connect: 'outlook-card-connect',
                Tooltip: 'outlook-card-tooltip'
            }
        }
    });
export default function OutlookCard(param) {
    let { integration, isDisabled } = param;
    const { t: outlookTexts } = useLocaleKeys('integrations.availableApps.outlook');
    const { t: gmailTexts } = useLocaleKeys('integrations.availableApps.gmail');
    const { t: sharedTexts } = useLocaleKeys('integrations.availableApps.shared');
    const businessId = useStore(selectors.user.businessId);
    const { api } = useStoreActions();
    const changeLocation = useIoContext().browser.changeLocation;
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { logBI } = useStoreActions();
    const navigate = useNavigate();
    const { OutlookCardTID } = useMemo(()=>GetOutlookCardTID(), []);
    const connect = async ()=>{
        const providerDetails = getEmailProviderDetails(EmailOnBehalfTypes.Outlook);
        if (integrationModel.isLinked(integration)) {
            navigate(routes.notifications, {
                state: {
                    selectedTab: Tab.Client
                }
            });
            return;
        }
        logBI(providerDetails.biCodes.connect, {
            vendorId: businessId
        });
        if (businessId) {
            startLoader();
            const result = await handleRequest(api.notification.emailOnBehalf.connectEmailOnBehalf({
                businessId,
                provider: EmailOnBehalfTypes.Outlook,
                pageUrl: window.location.origin + window.location.pathname
            }));
            if (result.success) {
                changeLocation(result.data);
            } else {
                logBI(providerDetails.biCodes.authError, {
                    vendorId: businessId
                });
                showErrorToast(outlookTexts.failedToConnectOutlook(), "start-outlook-flow-error-toast");
            }
            stopLoader();
        } else {
            logBI(providerDetails.biCodes.authError, {
                vendorId: businessId
            });
            showErrorToast(sharedTexts.pleaseRefreshAndTryAgain(), 'no-business-id-error-toast');
        }
    };
    return /*#__PURE__*/ _jsx(IntegrationCard, {
        testId: OutlookCardTID.Card(),
        icon: /*#__PURE__*/ _jsx(OutlookLogo, {
            width: 40,
            height: 40
        }),
        title: outlookTexts.title(),
        subTitle: outlookTexts.subTitle(),
        mainAction: /*#__PURE__*/ _jsx(IntegrationCard.MainActionButton, {
            bi: {
                name: 'connect_outlook'
            },
            onClick: connect,
            testId: OutlookCardTID.Connect(),
            disabled: isDisabled,
            tooltip: isDisabled ? {
                placement: 'right',
                title: /*#__PURE__*/ _jsxs("div", {
                    "data-testid": OutlookCardTID.Tooltip(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "invertedStandard",
                            children: gmailTexts.onlyOneMailbox()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "invertedStandard",
                            children: outlookTexts.disconnectGmailFirst()
                        })
                    ]
                })
            } : undefined,
            children: integrationModel.isLinked(integration) ? sharedTexts.manageConnection() : sharedTexts.connectButton()
        })
    });
}
